import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ACTIONS from "../../../../redux/action.js";
import axios from "../../../../shared/axios/axiosProxy.js";
import { legalDocuments } from "../../../../shared/data/legalDocuments.js";
import i18n from "../../../../utils/i18n.js";
import ContactInfo from "../contactInfo/contactInfo.jsx";
import ContactSelectionChips from "../contactPage/ContactSelectionChips.jsx";
import contactPageClasses from "../contactPage/contactPage.module.scss";
import AccountingUpsellCard from "./AccountingUpsellCard.jsx";
import classes from "./registrationContactPage.module.scss";

export const isRegistrationContactNameValid = (companyctcname) => {
  const full = (companyctcname || "").trim();
  const i = full.indexOf(" ");
  return (
    i > 0 && full.slice(0, i).trim().length > 0 && full.slice(i + 1).trim().length > 0
  );
};

export const isRegistrationContactEmailValid = (email) => {
  const trimmed = (email || "").trim();
  if (!trimmed) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(trimmed);
};

export const isRegistrationContactPhoneValid = (companyPhone) =>
  (companyPhone || "").trim().length >= 8;

export const isRegistrationContactValid = (companyStore) =>
  isRegistrationContactNameValid(companyStore?.companyctcname) &&
  isRegistrationContactEmailValid(companyStore?.email) &&
  isRegistrationContactPhoneValid(companyStore?.companyPhone);

/**
 * Shared contact + submit step for the reg/vaf/kftreg wireframe — mirrors the
 * ASP ContactPage's own submit flow (createAspRequest → reqID → handleNext into
 * the matching *SuccessScreen with BookingSlots), just with a fixed-fee
 * recommendation banner instead of the dynamic ASP price band, since these are
 * flat-fee services rather than an accountant-match request.
 */
const RegistrationContactPage = ({
  recommendationTitle,
  priceLabel,
  priceAmount,
  chipFields,
  callbacksource,
  createAspRequest,
  onRequestError,
  handleNext,
}) => {
  const dispatch = useDispatch();
  const companyStore = useSelector((state) => state.company_store);
  const [isPriceDetailsExpanded, setIsPriceDetailsExpanded] = useState(false);

  const isNameValid = useMemo(
    () => isRegistrationContactNameValid(companyStore.companyctcname),
    [companyStore.companyctcname],
  );
  const isEmailValid = useMemo(
    () => isRegistrationContactEmailValid(companyStore.email),
    [companyStore.email],
  );
  const isPhoneValid = useMemo(
    () => isRegistrationContactPhoneValid(companyStore.companyPhone),
    [companyStore.companyPhone],
  );
  const isFormValid = isNameValid && isEmailValid && isPhoneValid;

  const [touchedFields, setTouchedFields] = useState({
    name: false,
    email: false,
    phone: false,
  });
  const showNameError = touchedFields.name && !isNameValid;
  const showEmailError = touchedFields.email && !isEmailValid;
  const showPhoneError = touchedFields.phone && !isPhoneValid;

  const handleNameBlur = useCallback(() => {
    setTouchedFields((prev) => (prev.name ? prev : { ...prev, name: true }));
  }, []);
  const handleEmailBlur = useCallback(() => {
    setTouchedFields((prev) => (prev.email ? prev : { ...prev, email: true }));
  }, []);
  const handlePhoneBlur = useCallback(() => {
    setTouchedFields((prev) => (prev.phone ? prev : { ...prev, phone: true }));
  }, []);

  const [reqDisabled, setReqDisabled] = useState(false);

  const handleAspRequest = useCallback(async () => {
    if (!isFormValid || typeof createAspRequest !== "function") return;

    const trimmedName = (companyStore.companyctcname || "").trim();
    const trimmedEmail = (companyStore.email || "").trim();
    const trimmedPhone = (companyStore.companyPhone || "").trim();

    dispatch(
      ACTIONS.addCompanyInfo({ id: "gdpr", data: true, stateObject: "company_store" }),
    );

    [
      { id: "companyctcname", data: trimmedName, current: companyStore.companyctcname },
      { id: "email", data: trimmedEmail, current: companyStore.email },
      { id: "companyPhone", data: trimmedPhone, current: companyStore.companyPhone },
    ].forEach(({ id, data, current }) => {
      if (current !== data) {
        dispatch(ACTIONS.addCompanyInfo({ id, data, stateObject: "company_store" }));
      }
    });

    setReqDisabled(true);
    try {
      const response = await createAspRequest({
        ...companyStore,
        companyctcname: trimmedName,
        email: trimmedEmail,
        companyPhone: trimmedPhone,
        gdpr: true,
        callbacksource,
      });

      const reqId = response?.data?.result?._id;
      if (!reqId) {
        console.error(
          "Registration request finished without reqID in response; not advancing step.",
          response?.data,
        );
        setReqDisabled(false);
        onRequestError?.();
        return;
      }

      handleNext?.();
    } catch (error) {
      console.error("Error during the creation of the registration request:", error);
      setReqDisabled(false);
      onRequestError?.();
    }
  }, [
    callbacksource,
    companyStore,
    createAspRequest,
    dispatch,
    handleNext,
    isFormValid,
    onRequestError,
  ]);

  const handleSubmitClick = useCallback(() => {
    if (reqDisabled) {
      return;
    }
    if (!isFormValid) {
      setTouchedFields({ name: true, email: true, phone: true });
      return;
    }
    handleAspRequest();
  }, [handleAspRequest, isFormValid, reqDisabled]);

  // accounting is reset to false once at each flow's own mount (regFlow.jsx /
  // kftRegFlow.jsx / vafFlow.jsx componentDidMount), not here — this step can
  // unmount/remount on Back/Forward navigation (STEPS[activeStep] renders
  // without a stable key), so resetting it here on every mount would clobber
  // a value the user already toggled on before navigating away and back.

  const newsletterLabel = <span>{i18n.t("searchflow.newslettercontact")}</span>;

  const handleAccountingChange = useCallback(
    (value) => {
      dispatch(
        ACTIONS.addCompanyInfo({ id: "accounting", data: value, stateObject: "company_store" }),
      );
      // SearchSchema has no standalone `accounting` field — services only
      // persist inside the `services` array, so this rewrites the whole
      // array from the flow's fixed service (legalentityfounding for
      // reg/kftreg, taxconsultingsimple for vaf) plus the toggle state.
      const services = [
        companyStore.legalentityfounding ? "legalentityfounding" : null,
        companyStore.taxconsultingsimple ? "taxconsultingsimple" : null,
        value ? "accounting" : null,
      ].filter(Boolean);
      // Also mirror it into company_store.services (not just the direct PATCH
      // below): the flow's updateSearchData() patches `{...company_store}`
      // wholesale on every step change, so a stale redux value would otherwise
      // overwrite this on the next step change.
      dispatch(
        ACTIONS.addCompanyInfo({ id: "services", data: services, stateObject: "company_store" }),
      );
      if (companyStore.searchID) {
        axios
          .patch(
            `/api/search/${companyStore.searchID}`,
            { services },
            { headers: { "Content-Type": "application/json" } },
          )
          .catch((error) => {
            console.error(
              "Error saving services to search:",
              error.response?.data || error.message,
            );
          });
      }
    },
    [
      companyStore.legalentityfounding,
      companyStore.searchID,
      companyStore.taxconsultingsimple,
      dispatch,
    ],
  );

  return (
    <div className={contactPageClasses.root}>
      <div className={contactPageClasses.scrollArea}>
        <div className={contactPageClasses.content}>
          <div className={contactPageClasses.mainColumn}>
            <div className={classes.offerBanner}>
              <h1 className={classes.offerTitle}>{recommendationTitle}</h1>
              <p className={classes.offerFeeLabel}>{priceLabel}</p>
              <button
                type="button"
                className={classes.priceToggle}
                onClick={() => setIsPriceDetailsExpanded((prev) => !prev)}
                aria-expanded={isPriceDetailsExpanded}
              >
                <span className={classes.priceToggleLabel}>
                  <span className={classes.offerPrice}>{priceAmount}</span>
                </span>
                <KeyboardArrowDownIcon
                  className={`${classes.priceToggleIcon} ${
                    isPriceDetailsExpanded ? classes.priceToggleIconOpen : ""
                  }`}
                  aria-hidden="true"
                />
              </button>
              <div
                className={`${classes.detailsPanel} ${
                  isPriceDetailsExpanded ? classes.detailsPanelOpen : ""
                }`}
                aria-hidden={!isPriceDetailsExpanded}
              >
                <div className={classes.detailsPanelInner}>
                  <p className={classes.detailsIntro}>
                    {i18n.t("searchflow.registrationpricedetailsintro")}
                  </p>
                  <ContactSelectionChips
                    className={classes.detailsChips}
                    allowedKeys={chipFields}
                  />
                </div>
              </div>
            </div>

            <div className={contactPageClasses.formPanel}>
              <div className={contactPageClasses.formSection}>
                <ContactInfo
                  splitNameFields
                  layout="contactGrid"
                  className={contactPageClasses.contactFields}
                  lastNameLabel={i18n.t("searchflow.lastnamelabel")}
                  firstNameLabel={i18n.t("searchflow.yourgivenname")}
                  emailLabel={i18n.t("searchflow.registrationemaillabel")}
                  phoneLabel={i18n.t("searchflow.registrationphonelabel")}
                  nameError={showNameError}
                  emailError={showEmailError}
                  phoneError={showPhoneError}
                  onNameBlur={handleNameBlur}
                  onEmailBlur={handleEmailBlur}
                  onPhoneBlur={handlePhoneBlur}
                  middleSlot={
                    <div className={`${contactPageClasses.newsletterRow} newsletterRow`}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="marketing"
                            name="marketing"
                            checked={Boolean(companyStore.marketing)}
                            onChange={(event) => {
                              dispatch(
                                ACTIONS.addCompanyInfo({
                                  id: event.target.id,
                                  data: event.target.checked,
                                  stateObject: "company_store",
                                }),
                              );
                            }}
                            color="primary"
                          />
                        }
                        label={newsletterLabel}
                      />
                    </div>
                  }
                />

                <AccountingUpsellCard
                  checked={Boolean(companyStore.accounting)}
                  onChange={handleAccountingChange}
                />
              </div>

              <div className={contactPageClasses.submitArea}>
                <p className={contactPageClasses.legalText}>
                  {i18n.t("searchflow.registrationlegalprefix")}{" "}
                  <a
                    className={contactPageClasses.legalLink}
                    rel="noopener noreferrer"
                    target="_blank"
                    href={legalDocuments.gdpr}
                  >
                    {i18n.t("gdpr.part2")}
                  </a>{" "}
                  {i18n.t("gdpr.part3")}{" "}
                  <a
                    className={contactPageClasses.legalLink}
                    rel="noopener noreferrer"
                    target="_blank"
                    href={legalDocuments.aszf}
                  >
                    {i18n.t("gdpr.part4")}
                  </a>
                  {i18n.t("gdpr.part5")}
                </p>
                <button
                  type="button"
                  className={contactPageClasses.submitButton}
                  onClick={handleSubmitClick}
                  aria-disabled={!isFormValid || reqDisabled}
                >
                  <span>
                    {reqDisabled
                      ? i18n.t("searchflow.submitting")
                      : i18n.t("general.bookingappointment")}
                  </span>
                  <ChevronRightIcon className={contactPageClasses.submitIcon} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationContactPage;
